
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractGroup } from '@/lib/types';
import { toast } from 'sonner';

export const useGroups = () => {
  const queryClient = useQueryClient();

  // Get all groups
  const useGroupsQuery = () => {
    return useQuery({
      queryKey: ['contract-groups'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('contract_groups')
          .select('*')
          .order('name');

        if (error) {
          console.error('Error fetching groups:', error);
          throw error;
        }

        return data.map((group: any) => ({
          ...group,
          createdAt: new Date(group.created_at),
          updatedAt: new Date(group.updated_at)
        })) as ContractGroup[];
      }
    });
  };

  // Get a single group by ID
  const useGroupQuery = (id: string) => {
    return useQuery({
      queryKey: ['contract-groups', id],
      queryFn: async () => {
        if (!id) return null;

        const { data, error } = await supabase
          .from('contract_groups')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching group:', error);
          throw error;
        }

        return {
          ...data,
          createdAt: new Date(data.created_at),
          updatedAt: new Date(data.updated_at)
        } as ContractGroup;
      },
      enabled: !!id
    });
  };

  // Create a new group
  const useCreateGroupMutation = () => {
    return useMutation({
      mutationFn: async (data: Omit<ContractGroup, 'id' | 'createdAt' | 'updatedAt' | 'user_id'>) => {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        
        if (!userId) {
          toast.error("User not authenticated");
          throw new Error("User not authenticated");
        }
        
        const { data: groupData, error } = await supabase
          .from('contract_groups')
          .insert([{
            name: data.name,
            description: data.description,
            user_id: userId,
            created_at: data.created_at,
            updated_at: data.updated_at
          }])
          .select()
          .single();

        if (error) {
          console.error('Error creating group:', error);
          throw error;
        }

        return {
          ...groupData,
          createdAt: new Date(groupData.created_at),
          updatedAt: new Date(groupData.updated_at)
        } as ContractGroup;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contract-groups'] });
        toast.success('Group created successfully');
      }
    });
  };

  // Update an existing group
  const useUpdateGroupMutation = () => {
    return useMutation({
      mutationFn: async (group: ContractGroup) => {
        const { data: groupData, error } = await supabase
          .from('contract_groups')
          .update({
            name: group.name,
            description: group.description,
            updated_at: group.updated_at || new Date().toISOString()
          })
          .eq('id', group.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating group:', error);
          throw error;
        }

        return {
          ...groupData,
          createdAt: new Date(groupData.created_at),
          updatedAt: new Date(groupData.updated_at)
        } as ContractGroup;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contract-groups'] });
        toast.success('Group updated successfully');
      }
    });
  };

  // Delete a group
  const useDeleteGroupMutation = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase
          .from('contract_groups')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting group:', error);
          throw error;
        }

        return id;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contract-groups'] });
        toast.success('Group deleted successfully');
      }
    });
  };

  return {
    useGroupsQuery,
    useGroupQuery,
    useCreateGroupMutation,
    useUpdateGroupMutation,
    useDeleteGroupMutation
  };
};
